// src/pages/UserManagement.jsx
import React, { useCallback, useEffect, useState, useContext, useMemo } from 'react';
import {
  Button,
  Paper,
  Stack,
  Typography,
  Select,
  MenuItem,
  Dialog,
  Menu,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Checkbox,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { Link } from 'react-router-dom';
import { toast } from '../utils/toast';
import { request } from '../utils/request'; // updated helper
import { ColorModeContext } from '../index';

export const UserManagement = () => {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";

  // State
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isPasswordEdit, setIsPasswordEdit] = useState(false); // true if dialog opened for password-only edit
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({});
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Delete dialog state (single)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Snackbar state
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");

  // menu for per-row actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const data = await request("role/dropdown", { method: "get" });
      // many APIs return { data: [...] } — handle gracefully
      setRoles((data && data.data) || data || []);
    } catch (err) {
      console.error("Error fetching roles:", err);
      showSnack("Failed to fetch roles", "error");
    }
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const data = await request("user", { method: "get" });
      setUsers(Array.isArray(data) ? data : (data && data.data) || []);
    } catch (err) {
      console.error("Error fetching users:", err);
      showSnack("Failed to fetch users", "error");
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, [fetchRoles, fetchUsers]);

  // Open dialog: new user, edit user (no password), or password-only edit
  // openDialog(user = null, editPassword = false)
  const openDialog = (user = null, editPassword = false) => {
    if (user) {
      setSelectedId(user._id || user.id || null);
      setIsEdit(true);
      setIsPasswordEdit(Boolean(editPassword));
      if (editPassword) {
        // Only show password fields (do not show existing hashed password)
        setFormData({
          _id: user._id,
          username: user.username || "",
          password: "",
          confirm_password: "",
        });
      } else {
        // Full edit — but DO NOT populate password (remove hashed password)
        const copy = { ...user };
        delete copy.password;
        // If profile_image exists and you need it, keep it
        setFormData(copy);
      }
    } else {
      // New user
      setSelectedId(null);
      setIsEdit(false);
      setIsPasswordEdit(false);
      setFormData({
        status: "active",
        password: "",
        confirm_password: "",
      });
    }
    setOpen(true);
  };
  const closeDialog = () => {
    setOpen(false);
    setIsPasswordEdit(false);
    setIsEdit(false);
    setSelectedId(null);
    setFormData({});
  };

  // Handle change
  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // password/confirm change helper
  const handlePasswordFieldChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // file change
  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setFormData((prev) => ({ ...prev, profile_image_file: file }));
  };

  // Snackbar helper
  const showSnack = (message, severity = "success") => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };


  // Snackbar close handler (FIX for no-undef)
const handleSnackClose = (event, reason) => {
  if (reason === 'clickaway') return;
  setSnackOpen(false);
};

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    if (
      formData.password ||
      formData.confirm_password ||
      isPasswordEdit ||
      (!isEdit && formData.password !== undefined)
    ) {
      if (!formData.password) {
        showSnack("Password is required", "error");
        setLoading(false);
        return;
      }
      if (
        formData.confirm_password !== undefined &&
        formData.password !== formData.confirm_password
      ) {
        showSnack("Passwords do not match", "error");
        setLoading(false);
        return;
      }
    }

    const requestBody = new FormData();
    const commonFields = [
      "first_name",
      "last_name",
      "username",
      "password",
      "email",
      "department",
      "designation",
      "roleId",
      "mobile",
      "emp_id",
      "status",
    ];

    if (isPasswordEdit) {
      requestBody.append("password", formData.password);
    } else {
      commonFields.forEach((field) => {
        if (typeof formData[field] !== "undefined" && formData[field] !== null) {
          requestBody.append(field, formData[field]);
        }
      });
    }

    if (formData.profile_image_file) {
      requestBody.append("profile_image", formData.profile_image_file);
    }

    if (isEdit && !isPasswordEdit) {
      await request("user", {
        method: "put",
        params: selectedId,
        body: requestBody,
      });
      toast("User updated", { variant: "success" });
      showSnack("User updated successfully", "success");
    } else if (isEdit && isPasswordEdit) {
      await request("user", {
        method: "put",
        params: selectedId,
        body: requestBody,
      });
      toast("Password updated", { variant: "success" });
      showSnack("Password updated successfully", "success");
    } else {
      await request("register", {
        method: "post",
        body: requestBody,
      });
      toast("User created", { variant: "success" });
      showSnack("User created successfully", "success");
    }

    await fetchUsers();
    closeDialog();
  } catch (err) {
    console.error("Save user error:", err);
    const message = err?.message || "Error saving user";
    toast("Error saving user", { variant: "error" });
    showSnack(message, "error");
  } finally {
    setLoading(false);
  }
};


  // Delete flow: open confirmation dialog (single)
  const handleDeleteClick = (user) => {
    setDeletingUser(user);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingUser(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser || !deletingUser._id) return;
    setDeleting(true);
    try {
      await request("user", { method: "delete", params: deletingUser._id });
      toast("User deleted", { variant: "success" });
      showSnack(`User "${deletingUser.username || deletingUser.email}" deleted.`, "success");
      await fetchUsers();
      handleCancelDelete();
    } catch (err) {
      console.error("Error deleting user:", err);
      toast("Error deleting user", { variant: "error" });
      showSnack("Failed to delete user", "error");
      setDeleting(false);
    }
  };

  // -----------------------
  // Selection helpers (bulk / edit)
  // -----------------------

  // sorted list
  const sorted = [...users].sort((a, b) =>
    (a.first_name || '').localeCompare(b.first_name || '')
  );

  // visible ids (from the list you render)
  const visibleIds = useMemo(() => sorted.map(u => String(u._id)), [sorted]);

  const toggleSelect = (id) => {
    const sid = String(id);
    setSelectedIds(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(visibleIds.slice());
    } else {
      setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
    }
  };

  const isAllSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));

  const handleEditSelected = () => {
    if (selectedIds.length !== 1) return;
    const user = sorted.find(u => String(u._id) === selectedIds[0]);
    if (user) {
      openDialog(user);
      setSelectedIds([]); // optional: clear after opening
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => request('user', { method: 'delete', params: id })));
      showSnack(`${selectedIds.length} user(s) deleted.`, 'success');
      setSelectedIds([]);
      await fetchUsers();
      handleCancelBulkDelete();
    } catch (err) {
      console.error('Bulk delete error', err);
      showSnack('Failed to delete selected users.', 'error');
      setBulkDeleting(false);
    }
  };

  // Sorted is used in render below

  return (
    <Box p={2}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Link
              to="/home"
              style={{
                textDecoration: "none",
                color: mode === "dark" ? "#fff" : "#2d9cdb",
                transition: "color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#0b1d51";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = mode === "dark" ? "#fff" : "#2d9cdb";
              }}
            >
              Dashboard
            </Link>

            <Typography>/</Typography>
            <Typography color={isDark ? '#fff' : '#333'}>
              User Management
            </Typography>
          </Stack>
        </Box>
        <Button
          size="small"
          variant="contained"
          onClick={() => openDialog(null, false)}
          sx={{
            backgroundColor: '#2d9cdb', px: 2,
            py: 0.8,
            '&:hover': { backgroundColor: '#0b1d51', color: 'white' }
          }}
        >
          Add User
        </Button>
      </Stack>

      {/* Toolbar for selected rows */}
      {selectedIds.length > 0 && (
        <Box mb={1} display="flex" alignItems="center" gap={1}>
          <Typography>{selectedIds.length} selected</Typography>
          <Button size="small" variant="outlined" onClick={handleEditSelected} disabled={selectedIds.length !== 1}>Edit</Button>
          <Button size="small" variant="contained" onClick={handleDeleteSelected} sx={{ backgroundColor: '#dc3545', color: '#fff', '&:hover': { backgroundColor: '#b71c1c' } }}>
            Delete
          </Button>
          <Button size="small" onClick={() => setSelectedIds([])}>Clear</Button>
        </Box>
      )}

      {/* Table */}
      {/* ---------- Replace the existing Paper (table) block with this ---------- */}
      <Paper elevation={1} sx={{ mb: 2, height: 'calc(100vh - 170px)' }}>

        <Box sx={{ height: '100%', overflow: 'auto', width: '100%' }}>


          <Box
            component="table"
            width="100%"
            sx={{ borderCollapse: 'collapse', minWidth: '100%' }}

          >
            {/* Table head with sticky headers */}
            <Box component="thead">
              <Box component="tr">
                {/* Checkbox header */}
                <Box
                  component="th"
                  p={1}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 3,
                    backgroundColor: "#f5f7fb",
                    width: 48,
                    textAlign: 'center'
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    inputProps={{ 'aria-label': 'select all users' }}
                  />
                </Box>

                {[
                  '#',
                  'Username',
                  'Emp ID',
                  'First',
                  'Last',
                  'Email',
                  'Mobile',
                  'Role',
                  'Department',
                  'Actions'
                ].map((h, i) => (
                  <Box
                    component="th"
                    key={i}
                    p={1}
                    textAlign={i === 9 ? 'right' : 'left'}
                    // make each TH sticky so header remains visible while tbody scrolls
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "#f5f7fb",
                      color: isDark ? '#fff' : '#333'
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Table body */}
            <Box component="tbody">
              {sorted.map((u, idx) => {
                const roleName = roles.find(r => r._id === u.roleId)?.role || '—';
                return (
                  <Box component="tr" key={u._id}>
                    {/* row checkbox */}
                    <Box component="td" p={1} sx={{ width: 48, textAlign: 'center' }}>
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(String(u._id))}
                        onChange={() => toggleSelect(u._id)}
                        inputProps={{ 'aria-label': `select user ${u._id}` }}
                      />
                    </Box>

                    <Box component="td" p={1}>{idx + 1}</Box>
                    <Box component="td" p={1}>{u.username}</Box>
                    <Box component="td" p={1}>{u.emp_id || '—'}</Box>
                    <Box component="td" p={1}>{u.first_name}</Box>
                    <Box component="td" p={1}>{u.last_name}</Box>
                    <Box component="td" p={1}>{u.email}</Box>
                    <Box component="td" p={1}>{u.mobile}</Box>
                    <Box component="td" p={1}>{roleName}</Box>
                    <Box component="td" p={1}>{u.department || '—'}</Box>

                    {/* Actions column: three-dot menu (no edit/delete icons) */}
                    <Box component="td" p={1} textAlign="right">
                      <IconButton
                        size="small"
                        aria-label="more"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setMenuUser(u);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Menu for edit/delete (text items) */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => { setAnchorEl(null); setMenuUser(null); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              if (menuUser) openDialog(menuUser);
              setAnchorEl(null);
              setMenuUser(null);
            }}
          >
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuUser) handleDeleteClick(menuUser);
              setAnchorEl(null);
              setMenuUser(null);
            }}
          >
            Delete
          </MenuItem>
        </Menu>
      </Paper>
      {/* --------------------------------------------------------------------- */}

      {/* Bulk delete confirmation */}
      <Dialog open={bulkDeleteOpen} onClose={handleCancelBulkDelete}>
        <DialogTitle>Delete Selected Users</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the following {selectedIds.length} user(s)?</Typography>
          <Box sx={{ mt: 1 }}>
            {selectedIds.map(id => {
              const u = users.find(user => String(user._id) === String(id));
              return <Typography key={id}>• {u ? (u.username || u.email) : id}</Typography>;
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBulkDelete} disabled={bulkDeleting}>Cancel</Button>
          <Button onClick={handleConfirmBulkDelete} disabled={bulkDeleting} sx={{ backgroundColor:'#dc3545', '&:hover':{ backgroundColor:'#b71c1c'}, color:'#fff' }}>
            {bulkDeleting ? 'Deleting…' : `Yes, Delete (${selectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog */}
      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            backgroundColor: "#2d9cdb",
            color: "#fff",
            textAlign: "center",
            py: 1,
            fontSize: "1rem",
          }}
        >
          {isPasswordEdit ? "Reset Password" : isEdit ? "Edit User" : "Add User"}
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <DialogContent sx={{ p: 2 }}>
            <Grid container spacing={1}>
              {/* If this dialog is password-edit only, show only password fields */}
              {isPasswordEdit ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      name="password"
                      label="New Password"
                      type="password"
                      value={formData.password || ""}
                      onChange={handlePasswordFieldChange}
                      inputProps={{ style: { fontSize: "0.9rem" } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      name="confirm_password"
                      label="Confirm Password"
                      type="password"
                      value={formData.confirm_password || ""}
                      onChange={handlePasswordFieldChange}
                      inputProps={{ style: { fontSize: "0.9rem" } }}
                    />
                  </Grid>
                </>
              ) : (
                // Full user form (for add or edit user). password field is shown only for "Add User"
                <>
                  {[
                    { name: "first_name", label: "First Name" },
                    { name: "last_name", label: "Last Name" },
                    { name: "username", label: "User Name" },
                    // password intentionally not included for edit; included only for create
                    { name: "email", label: "Email", type: "email" },
                    { name: "department", label: "Department" },
                    { name: "designation", label: "Designation" },
                    { name: "mobile", label: "Mobile Number" },
                    { name: "emp_id", label: "Employee ID" },
                  ].map((field, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <TextField
                        fullWidth
                        size="small"
                        name={field.name}
                        type={field.type || "text"}
                        label={field.label}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        inputProps={{ style: { fontSize: "0.9rem" } }}
                      />
                    </Grid>
                  ))}

                  {/* Role select */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="label-role">Role</InputLabel>
                      <Select
                        labelId="label-role"
                        id="select-role"
                        name="roleId"
                        label="Role"
                        value={formData.roleId || ""}
                        onChange={handleChange}
                        inputProps={{ style: { fontSize: "0.9rem" } }}
                      >
                        <MenuItem value="">
                          <em>Select Role</em>
                        </MenuItem>
                        {roles.map((opt) => (
                          <MenuItem key={opt._id} value={opt._id}>
                            {opt.role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Status select */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="label-status">Status</InputLabel>
                      <Select
                        labelId="label-status"
                        id="select-status"
                        name="status"
                        label="Status"
                        value={formData.status || ""}
                        onChange={handleChange}
                        inputProps={{ style: { fontSize: "0.9rem" } }}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="suspended">Suspended</MenuItem>
                        <MenuItem value="deleted">Deleted</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Password only shown for Add User */}
                  {!isEdit && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          name="password"
                          label="Password"
                          type="password"
                          value={formData.password || ""}
                          onChange={handlePasswordFieldChange}
                          inputProps={{ style: { fontSize: "0.9rem" } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          name="confirm_password"
                          label="Confirm Password"
                          type="password"
                          value={formData.confirm_password || ""}
                          onChange={handlePasswordFieldChange}
                          inputProps={{ style: { fontSize: "0.9rem" } }}
                        />
                      </Grid>
                    </>
                  )}

                  {/* Profile image input & preview */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.9rem" }}>
                      Profile Image (optional)
                    </Typography>
                    <input
                      accept="image/*"
                      id="profile-image"
                      type="file"
                      onChange={handleFileChange}
                      style={{ display: "block", marginBottom: 8 }}
                    />
                    {formData.profile_image_file ? (
                      <img
                        alt="preview"
                        src={URL.createObjectURL(formData.profile_image_file)}
                        style={{
                          maxWidth: 120,
                          maxHeight: 120,
                          borderRadius: 8,
                          boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                        }}
                      />
                    ) : isEdit && formData.profile_image ? (
                      <img
                        alt="current"
                        src={formData.profile_image}
                        style={{
                          maxWidth: 120,
                          maxHeight: 120,
                          borderRadius: 8,
                          boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                        }}
                      />
                    ) : null}
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 1, pr: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={closeDialog}
              sx={{ color: '#2d9cdb', borderColor: 'white' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="small"
              variant="contained"
              sx={{
                backgroundColor: '#2d9cdb',
                '&:hover': { backgroundColor: '#0b1d51', color: 'white' }
              }}
              disabled={loading}
            >
              {isPasswordEdit ? "Update Password" : isEdit ? "Update" : "Submit"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog (single) */}
      <Dialog open={deleteOpen} onClose={handleCancelDelete} fullWidth maxWidth="xs">
        <DialogTitle sx={{ px: 2, pt: 2, fontSize: "0.95rem" }}>Delete User</DialogTitle>
        <DialogContent sx={{ px: 2 }}>
          <Typography sx={{ fontSize: "0.9rem" }}>
            Are you sure you want to delete the user{" "}
            <strong>{deletingUser ? deletingUser.username || deletingUser.email : ""}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", fontSize: "0.85rem" }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button size="small" onClick={handleCancelDelete} disabled={deleting} sx={{ fontSize: "0.85rem" }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            sx={{ backgroundColor: "#dc3545", "&:hover": { backgroundColor: "#b71c1c" }, fontSize: "0.85rem" }}
          >
            {deleting ? "Deleting…" : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Snackbar */}
      <Snackbar open={snackOpen} autoHideDuration={3000} onClose={handleSnackClose} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: "100%", fontSize: "0.9rem" }}>
          {snackMsg}
        </Alert>
      </Snackbar>


    </Box>
  );
};
